import { RiskRegisterAssessmentStatusEnum } from 'src/app/enums/assessmentStatus.enum';
import { OperatorTypeEnum } from 'src/app/enums/operatorType.enum';
import { RiskRegister } from 'src/app/models/risk-assessment.model';
import { RiskRegisterBulkTarget } from 'src/app/models/risk-register-bulk-update.model';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { EDITABLE_RISK_REGISTER_STATUSES, isApprovedRiskRegisterStatus } from 'src/app/shared/utils/risk-register-status.util';

export interface RiskRegisterSelection {
    agentIds: ReadonlySet<string>;
    excluded: ReadonlyMap<string, string>;
    explicit: ReadonlyMap<string, string>;
}

export interface RiskRegisterSelectionEntry {
    id: string;
    agentId: string;
}

export interface RiskRegisterSelectionScope {
    riskAssessmentId: string;
    filterBy: string;
    search: string;
    allAgentIds: string[];
}

export interface RiskRegisterEditableSelection {
    selection: RiskRegisterSelection;
    count: number;
}

const EDITABLE_STATUS_FILTER_KEY = 'OperatorTypeEnum.editableStatusGuard';

export function emptySelection(): RiskRegisterSelection {
    return { agentIds: new Set(), excluded: new Map(), explicit: new Map() };
}

export function isRegisterSelected(selection: RiskRegisterSelection, id: string, agentId: string): boolean {
    return selection.agentIds.has(agentId) ? !selection.excluded.has(id) : selection.explicit.has(id);
}

export function setRegisterSelected(
    selection: RiskRegisterSelection,
    id: string,
    agentId: string,
    selected: boolean,
): RiskRegisterSelection {
    if (selection.agentIds.has(agentId)) {
        const excluded = new Map(selection.excluded);
        if (selected) {
            excluded.delete(id);
        } else {
            excluded.set(id, agentId);
        }
        return { ...selection, excluded };
    }
    const explicit = new Map(selection.explicit);
    if (selected) {
        explicit.set(id, agentId);
    } else {
        explicit.delete(id);
    }
    return { ...selection, explicit };
}

function withoutAgent(entries: ReadonlyMap<string, string>, agentId: string): Map<string, string> {
    return new Map([...entries].filter(([, entryAgentId]) => entryAgentId !== agentId));
}

export function setColumnSelected(
    selection: RiskRegisterSelection,
    agentId: string,
    selected: boolean,
): RiskRegisterSelection {
    const agentIds = new Set(selection.agentIds);
    if (selected) {
        agentIds.add(agentId);
    } else {
        agentIds.delete(agentId);
    }
    return {
        agentIds,
        excluded: withoutAgent(selection.excluded, agentId),
        explicit: selected ? withoutAgent(selection.explicit, agentId) : selection.explicit,
    };
}

export function selectAll(allAgentIds: string[]): RiskRegisterSelection {
    return { agentIds: new Set(allAgentIds), excluded: new Map(), explicit: new Map() };
}

export function isColumnFullySelected(selection: RiskRegisterSelection, agentId: string): boolean {
    return selection.agentIds.has(agentId) && ![...selection.excluded.values()].includes(agentId);
}

export function areAllColumnsSelected(selection: RiskRegisterSelection, allAgentIds: string[]): boolean {
    return allAgentIds.length > 0 && allAgentIds.every((agentId) => selection.agentIds.has(agentId));
}

export function countSelection(selection: RiskRegisterSelection, columnCounts: ReadonlyMap<string, number>): number {
    const inColumns = [...selection.agentIds].reduce((sum, agentId) => sum + (columnCounts.get(agentId) ?? 0), 0);
    return Math.max(0, inColumns - selection.excluded.size) + selection.explicit.size;
}

export function restrictSelection(
    selection: RiskRegisterSelection,
    keep: { selector: boolean; explicit: boolean },
): RiskRegisterSelection {
    return {
        agentIds: keep.selector ? selection.agentIds : new Set(),
        excluded: keep.selector ? selection.excluded : new Map(),
        explicit: keep.explicit ? selection.explicit : new Map(),
    };
}

export function addExplicit(selection: RiskRegisterSelection, entries: RiskRegisterSelectionEntry[]): RiskRegisterSelection {
    const explicit = new Map(selection.explicit);
    entries
        .filter((entry) => !selection.agentIds.has(entry.agentId))
        .forEach((entry) => explicit.set(entry.id, entry.agentId));
    return { ...selection, explicit };
}

export function deselectRegisters(selection: RiskRegisterSelection, ids: ReadonlySet<string>): RiskRegisterSelection {
    const without = (entries: ReadonlyMap<string, string>) => new Map([...entries].filter(([id]) => !ids.has(id)));
    return { ...selection, excluded: without(selection.excluded), explicit: without(selection.explicit) };
}

export function editableStatusFilter(): Record<string, string> {
    return {
        [EDITABLE_STATUS_FILTER_KEY]: JSON.stringify({
            Condition: OperatorTypeEnum.Equal.toString(),
            Field: nameOf<RiskRegister>((x) => x.assessmentStatus),
            Value: EDITABLE_RISK_REGISTER_STATUSES.join(','),
        }),
    };
}

export function restrictToEditable(
    selection: RiskRegisterSelection,
    statusById: ReadonlyMap<string, RiskRegisterAssessmentStatusEnum>,
    editableCountByAgent: ReadonlyMap<string, number>,
): RiskRegisterEditableSelection {
    const isKnownEditable = (id: string) => statusById.has(id) && !isApprovedRiskRegisterStatus(statusById.get(id));
    const explicit = new Map([...selection.explicit].filter(([id]) => isKnownEditable(id)));
    const inColumns = [...selection.agentIds].reduce((sum, agentId) => sum + (editableCountByAgent.get(agentId) ?? 0), 0);
    const excludedEditable = [...selection.excluded.keys()].filter(isKnownEditable).length;
    const selectorCount = inColumns - excludedEditable;
    if (selectorCount <= 0) {
        return { selection: { agentIds: new Set(), excluded: new Map(), explicit }, count: explicit.size };
    }
    return { selection: { ...selection, explicit }, count: selectorCount + explicit.size };
}

export function buildBulkUpdateTargets(
    selection: RiskRegisterSelection,
    scope: RiskRegisterSelectionScope,
): RiskRegisterBulkTarget[] {
    const targets: RiskRegisterBulkTarget[] = [];
    if (selection.agentIds.size) {
        targets.push({
            selector: {
                riskAssessmentId: scope.riskAssessmentId,
                filterBy: scope.filterBy,
                search: scope.search,
                agentIds: areAllColumnsSelected(selection, scope.allAgentIds) ? null : [...selection.agentIds],
                excludedRiskRegisterIds: [...selection.excluded.keys()],
            },
        });
    }
    if (selection.explicit.size) {
        targets.push({ riskRegisterIds: [...selection.explicit.keys()] });
    }
    return targets;
}
